import seedrandom from 'seedrandom';
import { isContributionsOnlyCountry } from 'helpers/contributions';
import * as cookie from 'helpers/storage/cookie';
import { getQueryParameter } from 'helpers/urls/url';
import { tests } from './abtestDefinitions';
import { breakpoints } from './models';
import { getMvtId, MVT_MAX } from './mvt';
import { getSessionParticipations, PARTICIPATIONS_KEY, setSessionParticipations, } from './sessionStorage';
export const testIsActive = (value) => value[1] !== undefined;
function init({ countryId, countryGroupId, abTests = tests, mvt = getMvtId(), acquisitionDataTests = getTestFromAcquisitionData() ?? [], pathWithQueryString = window.location.pathname + window.location.search, }) {
    const inContributionsOnlyCountry = isContributionsOnlyCountry(countryId);
    const sessionParticipations = getSessionParticipations(PARTICIPATIONS_KEY);
    const participations = getParticipations(abTests, mvt, countryId, countryGroupId, inContributionsOnlyCountry, pathWithQueryString, acquisitionDataTests, sessionParticipations);
    const urlParticipations = getParticipationsFromUrl();
    const serverSideParticipations = getServerSideParticipations();
    return {
        ...participations,
        ...serverSideParticipations,
        ...urlParticipations,
    };
}
// ----- Helpers ----- //
function getParticipations(abTests, mvtId, country, countryGroupId, inContributionsOnlyCountry, pathWithQueryString, acquisitionDataTests, sessionParticipations = {}) {
    const participations = {};
    Object.entries(abTests).forEach(([testId, test]) => {
        if (!test.isActive) {
            return;
        }
        if (test.canRun && !test.canRun()) {
            return;
        }
        if (test.omitCountries?.includes(country)) {
            return;
        }
        // Is the user already in this test in the current browser session?
        if (test.persistPage &&
            !!sessionParticipations[testId] &&
            targetPageMatches(pathWithQueryString, test.persistPage)) {
            participations[testId] = sessionParticipations[testId];
            return;
        }
        if (!targetPageMatches(pathWithQueryString, test.targetPage)) {
            return;
        }
        const includeOnlyContributionsOnlyCountries = !!test.audiences.CONTRIBUTIONS_ONLY;
        /**
         * Exclude users in VAT compliance countries
         * from an ab test if the ab test definition has excludeContributionsOnlyCountries as true
         * AND includeOnlyContributionsOnlyCountries is not true
         */
        if (inContributionsOnlyCountry &&
            test.excludeContributionsOnlyCountries &&
            !includeOnlyContributionsOnlyCountries) {
            return;
        }
        /**
         * Exclude users outside VAT compliance countries
         * if the  the ab test definition has includeOnlyContributionsOnlyCountries as true
         */
        if (!inContributionsOnlyCountry && includeOnlyContributionsOnlyCountries) {
            return;
        }
        const participation = getUserParticipation(test, testId, mvtId, country, countryGroupId, acquisitionDataTests);
        if (participation.type === 'NO_PARTICIPATION') {
            return;
        }
        const variantAssignment = assignUserToVariant(mvtId, test, participation);
        if (variantAssignment.type === 'NOT_ASSIGNED') {
            return;
        }
        const testVariantId = test.variants[variantAssignment.variantIndex]?.id;
        if (testVariantId) {
            participations[testId] = testVariantId;
        }
    });
    // If referrerControlled is set for any test, exclude tests that have excludeIfInReferrerControlledTest set
    const inReferrerControlledTest = Object.keys(participations).some((testId) => abTests[testId]?.referrerControlled);
    if (inReferrerControlledTest) {
        Object.keys(participations).forEach((testId) => {
            if (abTests[testId]?.excludeIfInReferrerControlledTest) {
                delete participations[testId];
            }
        });
    }
    // Store participations which use the persistPage prop in sessionStorage
    Object.keys(participations).forEach((testId) => {
        if (abTests[testId]?.persistPage) {
            sessionParticipations[testId] = participations[testId];
        }
    });
    setSessionParticipations(sessionParticipations, PARTICIPATIONS_KEY);
    return participations;
}
function getParticipationsFromUrl() {
    const hashUrl = new URL(document.URL).hash;
    if (hashUrl.startsWith('#ab-')) {
        const [testId, variant] = decodeURI(hashUrl.substr(4)).split('=');
        if (testId && variant) {
            return { [testId]: variant };
        }
    }
    return;
}
function getServerSideParticipations() {
    if (window.guardian.serversideTests) {
        return window.guardian.serversideTests;
    }
    return null;
}
function getTestFromAcquisitionData() {
    const acquisitionDataParam = getQueryParameter('acquisitionData');
    if (!acquisitionDataParam) {
        return undefined;
    }
    try {
        const acquisitionData = JSON.parse(acquisitionDataParam);
        return acquisitionData.abTest
            ? [acquisitionData.abTest]
            : acquisitionData.abTests;
    }
    catch {
        console.error('Cannot parse acquisition data from query string');
        return undefined;
    }
}
function userInBreakpoint(audience) {
    if (!audience.breakpoint) {
        return true;
    }
    const { minWidth, maxWidth } = audience.breakpoint;
    if (!(minWidth ?? maxWidth)) {
        return true;
    }
    const minWidthMediaQuery = minWidth
        ? `(min-width:${breakpoints[minWidth]}px)`
        : null;
    const maxWidthMediaQuery = maxWidth
        ? `(max-width:${breakpoints[maxWidth]}px)`
        : null;
    const mediaQuery = minWidthMediaQuery && maxWidthMediaQuery
        ? `${minWidthMediaQuery} and ${maxWidthMediaQuery}`
        : minWidthMediaQuery ?? maxWidthMediaQuery;
    if (typeof mediaQuery === 'string') {
        return window.matchMedia(mediaQuery).matches;
    }
    return false;
}
function getUserParticipation(test, testId, mvtId, country, countryGroupId, acquisitionDataTests) {
    const { audiences, referrerControlled } = test;
    if (cookie.get('_post_deploy_user')) {
        return NO_PARTICIPATION;
    }
    const audience = audiences[country] ??
        audiences[countryGroupId] ??
        audiences.ALL ??
        audiences.CONTRIBUTIONS_ONLY;
    if (!audience) {
        return NO_PARTICIPATION;
    }
    if (referrerControlled) {
        // For referrer controlled tests we have to search through the tests in the acquisition
        // data to find a match. We use the `startsWith` method to support test campaigns all
        // with a common prefix.
        const acquisitionDataTest = acquisitionDataTests?.find((acquisitionDataTest) => acquisitionDataTest.name.startsWith(testId));
        if (!acquisitionDataTest) {
            return NO_PARTICIPATION;
        }
        return referrerControlledActiveParticipation(acquisitionDataTest);
    }
    const testMin = MVT_MAX * audience.offset;
    const testMax = testMin + MVT_MAX * audience.size;
    return mvtId >= testMin && mvtId < testMax && userInBreakpoint(audience)
        ? PARTICIPATING
        : NO_PARTICIPATION;
}
const NO_PARTICIPATION = { type: 'NO_PARTICIPATION' };
const PARTICIPATING = { type: 'ACTIVE_PARTICIPATION' };
const referrerControlledActiveParticipation = (acquisitionDataTest) => ({
    type: 'REFERRER_CONTROLLED_ACTIVE_PARTICIPATION',
    acquisitionDataTest,
});
function randomNumber(mvtId, seed) {
    const rng = seedrandom(`${mvtId + seed}`);
    return Math.abs(rng.int32());
}
function assignUserToVariant(mvtId, test, participation) {
    // For non-referrrer controlled tests we assign the user randomly
    if (participation.type === 'ACTIVE_PARTICIPATION') {
        return assigned(randomNumber(mvtId, test.seed) % test.variants.length);
    }
    // For referrrer controlled tests the assignment comes from the acquisition data.
    // If the variant in the acquisition data doesn't match up with any defined in
    // the test, we log an error and don't assign the user to the test.
    const acquisitionVariant = participation.acquisitionDataTest.variant;
    const index = test.variants.findIndex((variant) => variant.id === acquisitionVariant);
    if (index === -1) {
        console.error('Variant not found for A/B test in acquistion data');
        return NOT_ASSIGNED;
    }
    return assigned(index);
}
const NOT_ASSIGNED = { type: 'NOT_ASSIGNED' };
function assigned(variantIndex) {
    return { type: 'ASSIGNED', variantIndex };
}
function targetPageMatches(locationPath, targetPage) {
    if (!targetPage) {
        return true;
    }
    return locationPath.match(targetPage) != null;
}
export { init };
// Exported for testing only
export const _ = {
    targetPageMatches,
};
